import React, { useEffect, useState, useRef } from "react";
import SignatureCanvas from "react-signature-canvas";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./horizonTable.css";
import { toast } from "react-toastify";
import { addLogoAndFormHeader, applyFooterAllPages, getInnerTableWidth, buildSharePointFileName, toDdMmYyyy, addedByName } from "../../utils/formPdfHelpers.js";
import AdminTaylorImportModal, { adminIsPrimaryAdmin } from "./AdminTaylorImportModal";
import { ensureFormUuid, noopCreateSuccess } from "../../utils/formUuid";

import { canEditDeleteRecords } from "../../utils/rolePermissions";
function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL;
  return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
}

function getRefusalDirectorSignatureUrl(recordId) {
  if (!recordId) return "#";
  const pub = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  return `${window.location.origin}${pub}/form/refusal-to-work-form/sign/${recordId}`;
}

function hasDirectorSignature(record) {
  const sig = record?.directorSignature;
  if (sig == null) return false;
  return String(sig).trim().length > 0;
}

function getDirectorEmailSendError(directors) {
  if (!Array.isArray(directors) || directors.length === 0) {
    return "No director record found. Add a director in Settings → Director before sending the signature link.";
  }
  const hasEmail = directors.some((d) => String(d?.email || "").trim());
  if (!hasEmail) {
    return "Director email is missing. Add or update the director in Settings → Director before sending the signature link.";
  }
  return "";
}

function formatDate(d) {
  if (!d) return "—";
  const x = new Date(d);
  return isNaN(x.getTime()) ? "—" : x.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function RefusalToWorkFormPage() {
  const canEditDelete = canEditDeleteRecords();
  const [forms, setForms] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [appUsers, setAppUsers] = useState([]);
  const [directors, setDirectors] = useState([]);
  const [search, setSearch] = useState("");
  const sigSupervisorRef = useRef(null);
  const sigEmployeeRef = useRef(null);
  const sigClientRepRef = useRef(null);
  const sigDirectorRef = useRef(null);
  const [page, setPage] = useState(1);
  const pageSize = 4;

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const isAdminUser = adminIsPrimaryAdmin();
  const [editForm, setEditForm] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [sendingDirectorEmailId, setSendingDirectorEmailId] = useState(null);

  const [newForm, setNewForm] = useState({
    formUuid: "",
    complainantName: "",
    dateOfRefusal: "",
    siteLocation: "",
    timeOfRefusal: "",
    supervisorName: "",
    detailsOfConcernsRaised: "",
    detailsOfActionsTakenBySupervisor: "",
    supervisorSignature: "",
    employeeSignature: "",
    employeeNameInCapitals: "",
    employeePosition: "",
    employeeClientCompany: "",
    clientRepSignature: "",
    clientRepNameInCapitals: "",
    clientRepPosition: "",
    clientRepClientCompany: "",
    actionsTakenByTTSLSMT: "",
    dateResolvedClosedOut: "",
    directorSignature: "",
  });

  useEffect(() => {
    fetchForms();
    fetchAppUsers();
    fetchDirectors();
  }, []);

  async function fetchForms() {
    setLoadingRecords(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/refusal-to-work-form", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success && Array.isArray(data.data)) {
        setForms(data.data);
      } else {
        setForms([]);
      }
    } catch {
      setForms([]);
    } finally {
      setLoadingRecords(false);
    }
  }

  async function fetchAppUsers() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/app-users", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success && Array.isArray(data.data)) {
        setAppUsers(data.data);
      } else {
        setAppUsers([]);
      }
    } catch {
      setAppUsers([]);
    }
  }

  async function fetchDirectors() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/directors", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success && Array.isArray(data.data)) {
        setDirectors(data.data);
        return data.data;
      }
      setDirectors([]);
      return [];
    } catch {
      setDirectors([]);
      return [];
    }
  }

  const saveSignature = (key, ref) => {
    if (ref?.current && !ref.current.isEmpty()) {
      setNewForm((prev) => ({ ...prev, [key]: ref.current.getTrimmedCanvas().toDataURL("image/png") }));
    }
  };
  const clearSignature = (key, ref) => {
    if (ref?.current) ref.current.clear();
    setNewForm((prev) => ({ ...prev, [key]: "" }));
  };

  const filtered = forms.filter((f) => {
    const fields = [f.complainantName, f.siteLocation, f.supervisorName].map((v) => (v || "").toLowerCase());
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (words.length === 0) return true;
    return words.every((w) => fields.some((field) => field.includes(w)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice().slice((page - 1) * pageSize, page * pageSize);

  const getUserName = (record) => {
    if (record.addedByFirstName || record.addedByLastName) {
      return `${record.addedByFirstName || ""} ${record.addedByLastName || ""}`.trim();
    }

    const uid = record.userId || record.createdBy;
    const u = appUsers.find((x) => String(x._id) === String(uid));
    if (u) {
      const first = u.firstName || "";
      const last = u.lastName || "";
      const full = `${first} ${last}`.trim();
      return full || uid || "—";
    }
    return uid || "—";
  };

  const generatePDF = async (record, options = {}) => {
    const shouldUploadToSharePoint = options.uploadOnly === true;
    const uploadConfig = shouldUploadToSharePoint
      ? {
          uploadContext: options.uploadContext || "",
          onSuccess: options.onSuccess,
          onError: options.onError
        }
      : null;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const innerTableW = getInnerTableWidth(doc);
    let yPosition = await addLogoAndFormHeader(doc, "SF-005 Refusal To Work Form");
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");

    const leftX = 10;
    const formW = pageWidth - 20;
    const rightColW = formW * 0.33;
    const leftColW = formW - rightColW;
    const borderLW = 0.25;
    const labelFontSize = 10;
    const valueFontSize = 9;
    const lineHLabel = 4.1;
    const lineHValue = 3.8;
    const signatureW = 50;
    const signatureH = 22;

    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(borderLW);

    const addSignatureToPDF = async (signatureData, x, y, maxWidth, maxHeight) => {
      if (!signatureData) return;
      let sigSrc = signatureData;
      if (!String(sigSrc).startsWith("data:image")) {
        sigSrc = `data:image/png;base64,${sigSrc}`;
      }

      return new Promise((resolve) => {
        const sigImg = new Image();
        sigImg.crossOrigin = "anonymous";

        sigImg.onload = () => {
          try {
            const aspectRatio = sigImg.width / sigImg.height;
            let imgWidth = maxWidth;
            let imgHeight = maxWidth / aspectRatio;

            if (imgHeight > maxHeight) {
              imgHeight = maxHeight;
              imgWidth = maxHeight * aspectRatio;
            }

            imgWidth = Math.min(imgWidth, maxWidth);
            imgHeight = Math.min(imgHeight, maxHeight);

            const scale = 4;
            const canvas = document.createElement("canvas");
            canvas.width = Math.round(imgWidth * scale);
            canvas.height = Math.round(imgHeight * scale);

            const ctx = canvas.getContext("2d");
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = "high";

            ctx.fillStyle = "#FFFFFF";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(sigImg, 0, 0, sigImg.width, sigImg.height, 0, 0, canvas.width, canvas.height);

            const highResDataUrl = canvas.toDataURL("image/png", 1.0);
            doc.addImage(highResDataUrl, "PNG", x, y, imgWidth, imgHeight, "", "NONE");
            resolve();
          } catch {
            try {
              doc.addImage(sigSrc, "PNG", x, y, maxWidth, maxHeight);
            } catch {}
            resolve();
          }
        };

        sigImg.onerror = () => resolve();
        sigImg.src = sigSrc;
      });
    };

    const drawRect = (x, y, w, h) => {
      doc.rect(x, y, w, h);
    };

    const drawMultilineText = (text, x, y, maxW, fontSize, maxLines) => {
      if (text == null) return;
      const safe = String(text);
      if (!safe) return;
      doc.setFont("helvetica", doc.getFont().fontStyle === "bold" ? "bold" : "normal");
      doc.setFontSize(fontSize);
      const lines = doc.splitTextToSize(safe, maxW);
      const limited = typeof maxLines === "number" ? lines.slice(0, maxLines) : lines;
      limited.forEach((ln, i) => doc.text(ln, x, y + i * (fontSize >= 10 ? lineHLabel : lineHValue)));
    };

    const drawLabelValueInline = (label, value, x, y) => {
      const safeLabel = String(label);
      const safeValue = value != null && value !== "" ? String(value) : "";
      doc.setFont("helvetica", "bold");
      doc.setFontSize(labelFontSize);
      doc.text(safeLabel, x, y);
      const tw = doc.getTextWidth(safeLabel);
      if (safeValue) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(valueFontSize);
        doc.text(safeValue, x + tw + 1, y);
      }
    };

    const topCellH = 8;
    const fieldRowH = 12;
    const detailsHeaderH = 8;
    const concernsH = 90;
    const actionsTotalH = 120;
    const supervisorReservedH = 36;
    const pageMarginBottom = 25;

    const drawTopInfo = () => {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(labelFontSize);
      drawRect(leftX, yPosition, formW, topCellH);
      doc.text("To be completed by the TTSL Site Supervisor.", leftX + 2, yPosition + 6);
      yPosition += topCellH;

      drawRect(leftX, yPosition, formW, topCellH);
      doc.text("Copy to be sent to the TTSL Directors / HSQE Manager", leftX + 2, yPosition + 6);
      yPosition += topCellH;
    };

    const fitTextToWidth = (text, maxWidth) => {
      const raw = text == null ? "" : String(text);
      if (!raw) return "";
      if (doc.getTextWidth(raw) <= maxWidth) return raw;
      const ellipsis = "...";
      const ellipsisWidth = doc.getTextWidth(ellipsis);
      let out = "";
      for (let i = 0; i < raw.length; i++) {
        const next = out + raw[i];
        if (doc.getTextWidth(next) + ellipsisWidth > maxWidth) break;
        out = next;
      }
      return `${out}${ellipsis}`;
    };

    const drawFieldRow = (leftLabel, leftValue, rightLabel, rightValue) => {
      drawRect(leftX, yPosition, leftColW, fieldRowH);
      drawRect(leftX + leftColW, yPosition, rightColW, fieldRowH);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(labelFontSize);
      const baseline = yPosition + 8;
      if (leftLabel) doc.text(leftLabel, leftX + 2, baseline);
      if (leftValue != null && leftValue !== "") {
        const tw = doc.getTextWidth(leftLabel || "");
        doc.setFont("helvetica", "normal");
        doc.setFontSize(valueFontSize);
        const leftValueX = leftX + 2 + tw + 1;
        const leftMaxW = Math.max(0, leftX + leftColW - 2 - leftValueX);
        doc.text(fitTextToWidth(leftValue, leftMaxW), leftValueX, baseline);
      }
      if (rightLabel) doc.text(rightLabel, leftX + leftColW + 2, baseline);
      if (rightValue != null && rightValue !== "") {
        const tw2 = doc.getTextWidth(rightLabel || "");
        doc.setFont("helvetica", "normal");
        doc.setFontSize(valueFontSize);
        const rightValueX = leftX + leftColW + 2 + tw2 + 1;
        const rightMaxW = Math.max(0, leftX + leftColW + rightColW - 2 - rightValueX);
        doc.text(fitTextToWidth(rightValue, rightMaxW), rightValueX, baseline);
      }
      yPosition += fieldRowH;
    };

    const drawSectionHeader = (txt) => {
      drawRect(leftX, yPosition, formW, detailsHeaderH);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      const lines = doc.splitTextToSize(txt, formW - 4);
      lines.forEach((ln, i) => doc.text(ln, leftX + 2, yPosition + 6 + i * 4.1));
      yPosition += detailsHeaderH;
    };

    const drawConcernsBox = () => {
      drawRect(leftX, yPosition, formW, concernsH);
      const label = "Details of Concerns Raised (i.e. Working Practice, Unacceptable Risk or Hazard.):";
      doc.setFont("helvetica", "bold");
      doc.setFontSize(labelFontSize);
      const labelLines = doc.splitTextToSize(label, formW - 4);
      labelLines.forEach((ln, i) => doc.text(ln, leftX + 2, yPosition + 6 + i * lineHLabel));
      const valueText = record.detailsOfConcernsRaised || "";
      if (valueText) {
        const availableH = concernsH - (6 + labelLines.length * lineHLabel + 4);
        const maxLines = Math.max(0, Math.floor(availableH / lineHValue));
        const lines = doc.splitTextToSize(String(valueText), formW - 4).slice(0, maxLines);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(valueFontSize);
        lines.forEach((ln, i) => doc.text(ln, leftX + 2, yPosition + 20 + i * lineHValue));
      }
      yPosition += concernsH;
    };

    const drawActionsTopAndMaybeSplit = async () => {
      // Avoid splitting this section across pages (prevents duplicate label/text)
      if (yPosition + actionsTotalH > pageHeight - pageMarginBottom) {
        doc.addPage();
        yPosition = 20;
      }

      const topH = actionsTotalH;
      const bottomH = 0;

      drawRect(leftX, yPosition, formW, topH);
      const actionLabel =
        "Details of Actions taken by Supervisor in order to negotiate a return to work, (i.e. proposed Safer working method, negotiations with the Clients Site Manager or removal of the Hazard.)";
      doc.setFont("helvetica", "bold");
      doc.setFontSize(labelFontSize);
      const labelLines = doc.splitTextToSize(actionLabel, formW - 4);
      const signatureTop = yPosition + topH - supervisorReservedH;
      labelLines.forEach((ln, i) => {
        const lineY = yPosition + 6 + i * lineHLabel;
        if (lineY < signatureTop - 8) doc.text(ln, leftX + 2, lineY);
      });

      const textTop = yPosition + 20;
      const textBottom = signatureTop - 6;
      const availableTextH = Math.max(0, textBottom - textTop);
      const maxLines = Math.max(0, Math.floor(availableTextH / lineHValue));
      const actionValue = record.detailsOfActionsTakenBySupervisor || "";
      if (actionValue && maxLines > 0) {
        const lines = doc.splitTextToSize(String(actionValue), formW - 4).slice(0, maxLines);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(valueFontSize);
        lines.forEach((ln, i) => doc.text(ln, leftX + 2, textTop + i * lineHValue));
      }

      doc.setFont("helvetica", "bold");
      doc.setFontSize(labelFontSize);
      doc.text("Supervisor Signature:", leftX + 2, signatureTop + 6);
      const sigX = leftX + (formW - signatureW) / 2;
      const sigY = signatureTop + 10;
      await addSignatureToPDF(record.supervisorSignature, sigX, sigY, signatureW, signatureH);

      yPosition += topH;
      void bottomH;
    };

    const drawSignatureBlock = async (topLabelLines, signatureDataUrl, fieldLines) => {
      const boxH = 90;
      if (yPosition + boxH > pageHeight - pageMarginBottom) {
        doc.addPage();
        yPosition = 20;
      }
      const startY = yPosition;
      drawRect(leftX, startY, formW, boxH);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(labelFontSize);
      const headerY = startY + 6;
      const headerLinesLimited = topLabelLines.slice(0, 2);
      headerLinesLimited.forEach((ln, i) => doc.text(ln, leftX + 2, headerY + i * lineHLabel));

      const sigX = leftX + (formW - signatureW) / 2;
      const sigY = startY + 18;
      await addSignatureToPDF(signatureDataUrl, sigX, sigY, signatureW, signatureH);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9.2);
      fieldLines.forEach((f, i) => {
        const y = startY + 50 + i * 7;
        doc.text(f.label, leftX + 2, y);
        if (f.value) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9.2);
          const tw = doc.getTextWidth(f.label);
          doc.text(f.value, leftX + 2 + tw + 2, y);
          doc.setFont("helvetica", "bold");
          doc.setFontSize(9.2);
        }
      });

      yPosition += boxH;
    };

    const drawTtslDirectorBox = async () => {
      const boxH = 90;
      if (yPosition + boxH > pageHeight - pageMarginBottom) {
        doc.addPage();
        yPosition = 20;
      }
      drawRect(leftX, yPosition, formW, boxH);
      const label =
        "Details of ACTIONS taken by TTSL SMT (i.e. proposed Safer working method, negotiations with the Clients Site Manager or removal of the Hazard.)";
      doc.setFont("helvetica", "bold");
      doc.setFontSize(labelFontSize);
      const labelLines = doc.splitTextToSize(label, formW - 4);
      labelLines.slice(0, 2).forEach((ln, i) => doc.text(ln, leftX + 2, yPosition + 6 + i * lineHLabel));

      const valueText = record.actionsTakenByTTSLSMT || "";
      if (valueText) {
        const valueTop = yPosition + 26;
        const availableH = boxH - (valueTop - yPosition) - 28;
        const maxLines = Math.max(0, Math.floor(availableH / lineHValue));
        if (maxLines > 0) {
          const lines = doc.splitTextToSize(String(valueText), formW - 4).slice(0, maxLines);
          doc.setFont("helvetica", "normal");
          doc.setFontSize(valueFontSize);
          lines.forEach((ln, i) => doc.text(ln, leftX + 2, valueTop + i * lineHValue));
        }
      }

      const dateLabel = "Date issue resolved / Closed Out:";
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9.8);
      doc.text(dateLabel, leftX + 2, yPosition + 58);
      if (record.dateResolvedClosedOut) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9.8);
        const dateValue = ` ${formatDate(record.dateResolvedClosedOut)}`;
        const dateValueX = leftX + 2 + doc.getTextWidth(dateLabel) + 3;
        const dateMaxW = Math.max(0, leftX + formW - 2 - dateValueX);
        doc.text(fitTextToWidth(dateValue, dateMaxW), dateValueX, yPosition + 58);
      }

      doc.setFont("helvetica", "bold");
      doc.setFontSize(9.8);
      doc.text("Signature of TTSL Director", leftX + 2, yPosition + 68);
      const sigX = leftX + (formW - signatureW) / 2;
      const sigY = yPosition + 66;
      await addSignatureToPDF(record.directorSignature, sigX, sigY, signatureW, signatureH);

      yPosition += boxH;
    };

    drawTopInfo();
    drawFieldRow("Complainants Name:", record.complainantName, "Date :", formatDate(record.dateOfRefusal));
    drawFieldRow("Site / Location", record.siteLocation, "Time :", record.timeOfRefusal);
    drawFieldRow("Supervisor", record.supervisorName, "", "");
    drawSectionHeader("REFUSAL TO WORK DETAILS.");
    drawConcernsBox();
    await drawActionsTopAndMaybeSplit();

    await drawSignatureBlock(
      ["Signature of Employee", "(Raising the complaint, to endorse the details of this form are correct)"],
      record.employeeSignature,
      [
        { label: "Name in Capitals:", value: record.employeeNameInCapitals || "" },
        { label: "Position:", value: record.employeePosition || "" },
        { label: "Client Company:", value: record.employeeClientCompany || "" },
      ]
    );

    await drawSignatureBlock(
      ["Signature of Clients representative,", "(to endorse the details of this form are correct)"],
      record.clientRepSignature,
      [
        { label: "Name in Capitals:", value: record.clientRepNameInCapitals || "" },
        { label: "Position:", value: record.clientRepPosition || "" },
        { label: "Client Company:", value: record.clientRepClientCompany || "" },
      ]
    );

    if (yPosition + 90 > pageHeight - pageMarginBottom) {
      doc.addPage();
      yPosition = 20;
    }
    await drawTtslDirectorBox();

    applyFooterAllPages(doc);

    const safeName = String(record.complainantName || "Refusal").replace(/[\\/:"*?<>|]+/g, "-");
    const safeDate = String(formatDate(record.dateOfRefusal) || "form").replace(/[\\/:"*?<>|]+/g, "-");
    let fileName;
    if (shouldUploadToSharePoint && uploadConfig) {
      fileName = buildSharePointFileName([
        record.complainantName,
        record.siteLocation,
        toDdMmYyyy(record.dateOfRefusal),
      ]);
    } else {
      fileName = `SF005_${safeName}_${safeDate}.pdf`;
    }

    if (shouldUploadToSharePoint && uploadConfig) {
      try {
        const key = `${record._id || ""}|${record.complainantName || ""}|${record.dateOfRefusal || ""}`;
        setUploadingKey(key);
        const apiUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
        const dataUri = doc.output("datauristring");
        const uploadParams = {
          fileName,
          fileBase64: dataUri,
          uploadContext: uploadConfig.uploadContext, uploadSubFolder: "SF-005 Refusal To Work Form"
        };
        const res = await fetch(`${apiUrl}/api/sharepoint/upload`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
          body: JSON.stringify(uploadParams)
        });
        if (res.status !== 204 && !res.ok) {
          let msg = "SharePoint upload failed";
          try {
            const out = await res.json();
            msg = out?.message || msg;
          } catch {
            /* ignore */
          }
          throw new Error(msg);
        }
        toast.success("PDF uploaded to SharePoint successfully!");
        if (uploadConfig.onSuccess) uploadConfig.onSuccess();
      } catch (err) {
        toast.error(err?.message || "SharePoint upload failed");
        if (uploadConfig.onError) uploadConfig.onError(err);
      } finally {
        setUploadingKey(null);
      }
    } else {
      doc.save(fileName);
      toast.success("SF-005 Refusal To Work PDF generated successfully!");
    }
  };

  const handleAdd = async () => {
    try {
      const token = localStorage.getItem("token");
      let userId = localStorage.getItem("userId");
      if (!userId) {
        try {
          const localUser = JSON.parse(localStorage.getItem("user") || "null");
          userId = localUser?.id || localUser?._id;
        } catch {}
      }
      const { directorSignature: _omitDirectorSig, ...newFormWithoutDirectorSig } = newForm;
      const payload = {
        ...newFormWithoutDirectorSig,
        formUuid: ensureFormUuid(newForm.formUuid),
        userId: userId || undefined,
        dateOfRefusal: newForm.dateOfRefusal || undefined,
        dateResolvedClosedOut: newForm.dateResolvedClosedOut || undefined,
      };
      const res = await fetch(getBaseUrl() + "/api/refusal-to-work-form", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        if (noopCreateSuccess(data)) {
          toast.warn(data.message || "This record already exists. No new record was added.");
          return;
        }
        await fetchForms();
        setShowAddModal(false);
        setNewForm({
          formUuid: "",
          complainantName: "",
          dateOfRefusal: "",
          siteLocation: "",
          timeOfRefusal: "",
          supervisorName: "",
          detailsOfConcernsRaised: "",
          detailsOfActionsTakenBySupervisor: "",
          supervisorSignature: "",
          employeeSignature: "",
          employeeNameInCapitals: "",
          employeePosition: "",
          employeeClientCompany: "",
          clientRepSignature: "",
          clientRepNameInCapitals: "",
          clientRepPosition: "",
          clientRepClientCompany: "",
          actionsTakenByTTSLSMT: "",
          dateResolvedClosedOut: "",
          directorSignature: "",
        });
        if (sigSupervisorRef.current) sigSupervisorRef.current.clear();
        if (sigEmployeeRef.current) sigEmployeeRef.current.clear();
        if (sigClientRepRef.current) sigClientRepRef.current.clear();
        if (sigDirectorRef.current) sigDirectorRef.current.clear();
        toast.success("Form added!");
      } else {
        toast.error(data.message || "Failed to add");
      }
    } catch {
      toast.error("Failed to add");
    }
  };

  const handleEdit = (f) => {
    setEditForm({
      ...f,
      formUuid: ensureFormUuid(f?.formUuid),
      dateOfRefusal: f.dateOfRefusal ? new Date(f.dateOfRefusal).toISOString().slice(0, 10) : "",
      dateResolvedClosedOut: f.dateResolvedClosedOut ? new Date(f.dateResolvedClosedOut).toISOString().slice(0, 10) : "",
    });
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editForm || !editForm._id) return;
    try {
      const token = localStorage.getItem("token");
      const {
        directorSignature: _omitDirectorSig,
        actionsTakenByTTSLSMT: _omitSmt,
        dateResolvedClosedOut: _omitDate,
        ...editWithoutDirectorFields
      } = editForm;
      const payload = {
        ...editWithoutDirectorFields,
        formUuid: ensureFormUuid(editForm.formUuid),
        dateOfRefusal: editForm.dateOfRefusal || undefined,
      };
      delete payload._id;
      delete payload.createdAt;
      delete payload.updatedAt;
      const res = await fetch(getBaseUrl() + "/api/refusal-to-work-form/" + editForm._id, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchForms();
        setShowEditModal(false);
        setEditForm(null);
        toast.success("Form updated!");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/refusal-to-work-form/" + deleteId, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setForms(forms.filter((f) => f._id !== deleteId));
        toast.success("Deleted successfully!");
      } else {
        toast.error(data.message || "Failed to delete");
      }
    } catch {
      toast.error("Failed to delete");
    }
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const handleSendDirectorSignatureEmail = async (record) => {
    if (!record?._id || sendingDirectorEmailId || hasDirectorSignature(record)) return;

    const latestDirectors = await fetchDirectors();
    const directorError = getDirectorEmailSendError(latestDirectors);
    if (directorError) {
      toast.error(directorError);
      return;
    }

    setSendingDirectorEmailId(record._id);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${getBaseUrl()}/api/refusal-to-work-form/${record._id}/send-director-signature-email`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            signatureUrl: getRefusalDirectorSignatureUrl(record._id),
          }),
        }
      );
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message || "Director signature email sent");
      } else {
        toast.error(data.message || "Failed to send email");
      }
    } catch {
      toast.error("Failed to send email");
    } finally {
      setSendingDirectorEmailId(null);
    }
  };

  const directorOnlyReadOnlyFields = [
    {
      key: "actionsTakenByTTSLSMT",
      label:
        "Details of ACTIONS taken by TTSL SMT (i.e. proposed Safer working method, negotiations with the Clients Site Manager or removal of the Hazard.)",
      textarea: true,
      section: "ttsl",
    },
    { key: "dateResolvedClosedOut", label: "Date issue resolved / Closed Out", type: "date", section: "resolution" },
  ];

  const readOnlyValueStyle = {
    border: "2px solid #8b1634",
    borderRadius: "12px",
    padding: "12px 14px",
    fontSize: "14px",
    background: "#ffffff",
    color: "#1f2937",
    minHeight: "42px",
    whiteSpace: "pre-wrap",
  };

  const formFields = [
    { key: "complainantName", label: "Complainants Name", section: "initial" },
    { key: "dateOfRefusal", label: "Date", type: "date", section: "initial" },
    { key: "siteLocation", label: "Site / Location", section: "initial" },
    { key: "timeOfRefusal", label: "Time", type: "time", section: "initial" },
    { key: "supervisorName", label: "Supervisor", section: "initial" },
    { key: "detailsOfConcernsRaised", label: "Details of Concerns Raised (i.e. Working Practice, Unacceptable Risk or Hazard.)", textarea: true, section: "refusal" },
    { key: "detailsOfActionsTakenBySupervisor", label: "Details of Actions taken by Supervisor in order to negotiate a return to work, (i.e. proposed Safer working method, negotiations with the Clients Site Manager or removal of the Hazard.)", textarea: true, section: "refusal" },
    { key: "supervisorSignature", label: "Supervisor Signature", signature: true, section: "refusal", sigRef: "supervisor" },
    { key: "employeeSignature", label: "Signature of Employee (Raising the complaint, to endorse the details of this form are correct)", signature: true, section: "employee", sigRef: "employee" },
    { key: "employeeNameInCapitals", label: "Name in Capitals", section: "employee" },
    { key: "employeePosition", label: "Position", section: "employee" },
    { key: "employeeClientCompany", label: "Client Company", section: "employee" },
    { key: "clientRepSignature", label: "Signature of Clients representative (to endorse the details of this form are correct)", signature: true, section: "clientRep", sigRef: "clientRep" },
    { key: "clientRepNameInCapitals", label: "Name in Capitals", section: "clientRep" },
    { key: "clientRepPosition", label: "Position", section: "clientRep" },
    { key: "clientRepClientCompany", label: "Client Company", section: "clientRep" },
  ];

  const sectionOrder = ["initial", "refusal", "employee", "clientRep", "ttsl", "resolution"];
  const sectionLabels = {
    initial: "INCIDENT DETAILS",
    refusal: "REFUSAL TO WORK DETAILS",
    employee: "EMPLOYEE DETAILS",
    clientRep: "CLIENT'S REPRESENTATIVE DETAILS",
    ttsl: "DETAILS OF ACTIONS TAKEN BY TTSL SMT",
    resolution: "RESOLUTION",
  };
  const inputStyle = { border: "1px solid #e2e8f0", borderRadius: "8px", padding: "10px 12px", fontSize: "14px" };
  const labelStyle = { fontWeight: 600, color: "#8b1634", fontSize: "14px", marginBottom: "6px", display: "block" };
  const focusHandlers = {
    onFocus: (e) => { e.target.style.borderColor = "#8b1634"; e.target.style.boxShadow = "0 0 0 2px rgba(139,22,52,0.15)"; },
    onBlur: (e) => { e.target.style.borderColor = "#e2e8f0"; e.target.style.boxShadow = "none"; },
  };

  return (
    <>
      <div
        style={{
          background: "#ffffff",
          borderRadius: "20px",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
          padding: "24px",
          marginBottom: "24px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
            flexWrap: "wrap",
            gap: "16px",
          }}
        >
          <h2 style={{ fontSize: "24px", fontWeight: "700", color: "#1a202c", margin: 0 }}>
            SF-005 Refusal To Work Form
          </h2>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            {isAdminUser && (
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                style={{
                  background: "#8b1634",
                  color: "#fff",
                  border: "none",
                  borderRadius: "10px",
                  padding: "10px 14px",
                  fontWeight: "600",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                Import File
              </button>
            )}
          <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <i className="fa fa-search" style={{ position: "absolute", left: "12px", color: "#8b1634", fontSize: "14px" }} />
              <input
                type="text"
                placeholder="Type to Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                style={{
                  padding: "10px 12px 10px 36px",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  fontSize: "14px",
                  outline: "none",
                  width: "250px",
                  transition: "border-color 0.2s",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#8b1634")}
                onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
              />
            </div>
          </div>
        </div>

        <div style={{ overflowX: "auto", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1.2fr 0.9fr 0.8fr 1fr 1fr 0.9fr minmax(200px, 1.4fr)",
              background: "#f7fafc",
              padding: "12px 16px",
              borderBottom: "1px solid #e2e8f0",
              gap: "16px",
              alignItems: "center",
            }}
          >
            <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Complainant</div>
            <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Site Location</div>
            <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Date</div>
            <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Time</div>
            <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Supervisor</div>
            <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Added By</div>
            <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Updated Date</div>
            <div style={{ fontWeight: "600", color: "#8b1634", fontSize: "14px" }}>Actions</div>
          </div>
          <div>
            {loadingRecords ? (
              <div style={{ textAlign: "center", padding: "40px", color: "#8b1634", fontSize: "16px", fontWeight: 600 }}>
                <i className="fa fa-spinner fa-spin" style={{ marginRight: 8 }} />
                Loading records...
              </div>
            ) : paged.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px", color: "#718096", fontSize: "16px" }}>
                No records found.
              </div>
            ) : (
              paged.map((f, idx) => (
                  <div
                    key={f._id || idx}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1.2fr 0.9fr 0.8fr 1fr 1fr 0.9fr minmax(200px, 1.4fr)",
                      padding: "16px",
                      borderBottom: "1px solid #f1f5f9",
                      gap: "16px",
                      alignItems: "center",
                      transition: "background-color 0.2s",
                    }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f9fafb")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                >
                  <div style={{ color: "#2d3748", fontSize: "14px" }}>{f.complainantName || "—"}</div>
                  <div style={{ color: "#2d3748", fontSize: "14px" }}>{f.siteLocation || "—"}</div>
                  <div style={{ color: "#2d3748", fontSize: "14px" }}>{formatDate(f.dateOfRefusal)}</div>
                  <div style={{ color: "#2d3748", fontSize: "14px" }}>{f.timeOfRefusal || "—"}</div>
                  <div style={{ color: "#2d3748", fontSize: "14px" }}>{f.supervisorName || "—"}</div>
                  <div style={{ color: "#2d3748", fontSize: "14px" }}>{getUserName(f)}</div>
                  <div style={{ color: "#2d3748", fontSize: "14px" }}>{formatDate(f.updatedAt || f.createdAt || f.dateOfRefusal)}</div>
                  <div
                    style={{
                      display: "inline-flex",
                      flexWrap: "nowrap",
                      gap: "6px",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      width: "100%",
                    }}
                  >
                    {canEditDelete && (
                      <button
                        type="button"
                        onClick={() => handleEdit(f)}
                        title="Edit"
                        style={{
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          width: "32px",
                          height: "32px",
                          borderRadius: "8px",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f0fdf4")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      >
                        <i className="fa fa-edit" style={{ color: "#22c55e", fontSize: "16px" }} />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => generatePDF(f)}
                      title="Generate PDF"
                      style={{
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        width: "32px",
                        height: "32px",
                        borderRadius: "8px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#fef2f2")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                    >
                      <i className="fa fa-file-pdf-o" style={{ color: "#dc2626", fontSize: "16px" }} />
                    </button>
                    {(() => {
                      const k = `${f._id || ""}|${f.complainantName || ""}|${f.dateOfRefusal || ""}`;
                      const busy = uploadingKey === k;
                      return (
                        <button
                          type="button"
                          onClick={() => {
                            if (!busy) {
                              generatePDF(f, {
                                uploadOnly: true,
                                uploadContext: "hseq-forms-tablets",
                                onSuccess: () => {},
                                onError: () => {}
                              });
                            }
                          }}
                          disabled={busy}
                          title={busy ? "Uploading..." : "Upload to SharePoint"}
                          style={{
                            background: "transparent",
                            border: "none",
                            cursor: busy ? "not-allowed" : "pointer",
                            width: "32px",
                            height: "32px",
                            borderRadius: "8px",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            opacity: busy ? 0.6 : 1,
                            flexShrink: 0,
                          }}
                        >
                          <i className={busy ? "fa fa-spinner fa-spin" : "fa fa-upload"} style={{ color: "#22c55e", fontSize: "16px" }} />
                        </button>
                      );
                    })()}
                    {canEditDelete && (
                      <button
                        type="button"
                        onClick={() => handleDelete(f._id)}
                        title="Delete"
                        style={{
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          width: "32px",
                          height: "32px",
                          borderRadius: "8px",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#fef2f2")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      >
                        <i className="fa fa-trash" style={{ color: "#e53e3e", fontSize: "16px" }} />
                      </button>
                    )}
                    {(() => {
                      const emailBusy = sendingDirectorEmailId === f._id;
                      const directorSigned = hasDirectorSignature(f);
                      const emailDisabled = emailBusy || directorSigned;
                      return (
                        <button
                          type="button"
                          onClick={() => handleSendDirectorSignatureEmail(f)}
                          disabled={emailDisabled}
                          title={
                            directorSigned
                              ? "Director signature already recorded — email not available"
                              : "Send director signature link by email"
                          }
                          aria-label="Send director signature email"
                          style={{
                            background: "transparent",
                            border: "none",
                            width: "32px",
                            height: "32px",
                            borderRadius: "8px",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            color: directorSigned ? "#94a3b8" : "#8b1634",
                            cursor: emailDisabled ? "not-allowed" : "pointer",
                            opacity: emailDisabled ? 0.45 : 1,
                          }}
                          onMouseEnter={(e) => {
                            if (!emailDisabled) e.currentTarget.style.backgroundColor = "rgba(139,22,52,0.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "transparent";
                          }}
                        >
                          <i
                            className={emailBusy ? "fa fa-spinner fa-spin" : "fa fa-envelope"}
                            style={{ fontSize: "16px" }}
                            aria-hidden
                          />
                        </button>
                      );
                    })()}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: "18px", gap: "12px" }}>
          <button
            className="horizon-table-btn"
            disabled={page === 1 || filtered.length === 0}
            onClick={() => setPage(page - 1)}
            style={{
              padding: "8px 16px",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              background: page === 1 || filtered.length === 0 ? "#f7fafc" : "#ffffff",
              color: page === 1 || filtered.length === 0 ? "#a0aec0" : "#8b1634",
              cursor: page === 1 || filtered.length === 0 ? "not-allowed" : "pointer",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            Prev
          </button>
          <span style={{ fontWeight: "700", color: "#8b1634", fontSize: "16px" }}>
            {page} / {totalPages}
          </span>
          <button
            className="horizon-table-btn"
            disabled={page === totalPages || filtered.length === 0}
            onClick={() => setPage(page + 1)}
            style={{
              padding: "8px 16px",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              background: page === totalPages || filtered.length === 0 ? "#f7fafc" : "#ffffff",
              color: page === totalPages || filtered.length === 0 ? "#a0aec0" : "#8b1634",
              cursor: page === totalPages || filtered.length === 0 ? "not-allowed" : "pointer",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            Next
          </button>
        </div>
      </div>

      {showAddModal && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.4)" }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{ maxWidth: "1200px", margin: "0 auto", borderRadius: "12px", boxShadow: "0 20px 60px rgba(0,0,0,0.2)", border: "none" }}>
              <div className="modal-header" style={{ borderBottom: "1px solid #e2e8f0", background: "#f8fafc", padding: "16px 20px", borderRadius: "12px 12px 0 0" }}>
                <h5 className="modal-title" style={{ fontWeight: 600, color: "#8b1634", fontSize: "18px" }}>Add SF-005 Refusal To Work Form</h5>
                <button type="button" className="btn-close" onClick={() => setShowAddModal(false)} style={{ filter: "none", opacity: 0.7 }} />
              </div>
              <div className="modal-body" style={{ maxHeight: "75vh", overflowY: "auto", padding: "24px 20px", background: "#fff" }}>
                <div style={{ padding: "14px 16px", borderRadius: "10px", background: "rgba(139,22,52,0.08)", border: "1px solid rgba(139,22,52,0.3)", color: "#8b1634", fontSize: "14px", marginBottom: "24px", fontWeight: 500 }}>
                  To be completed by the TTSL Site Supervisor. Copy to be sent to the TTSL Directors / HSQE Manager
                </div>
                {sectionOrder.map((sectionKey) => {
                  const fields = formFields.filter((f) => f.section === sectionKey);
                  if (fields.length === 0) return null;
                  const label = sectionLabels[sectionKey];
                  const sigRefMap = { supervisor: sigSupervisorRef, employee: sigEmployeeRef, clientRep: sigClientRepRef };
                  return (
                    <div
                      key={sectionKey}
                      style={{
                        marginBottom: "24px",
                        borderRadius: "12px",
                        border: "1px solid #e2e8f0",
                        overflow: "hidden",
                        background: "#fff",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                      }}
                    >
                      <div
                        style={{
                          padding: "12px 20px",
                          background: "linear-gradient(135deg, rgba(139,22,52,0.08) 0%, rgba(139,22,52,0.04) 100%)",
                          borderBottom: "2px solid #8b1634",
                          fontWeight: 700,
                          color: "#8b1634",
                          fontSize: "14px",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {label}
                      </div>
                      <div className="row g-3" style={{ padding: "20px" }}>
                        {fields.map(({ key, label: fieldLabel, type, textarea, signature, sigRef }) => {
                          const ref = sigRef ? sigRefMap[sigRef] : null;
                          return (
                            <div key={key} className={textarea ? "col-12" : signature ? "col-md-6 col-lg-4" : "col-md-6"}>
                              <label className="form-label" style={labelStyle}>{fieldLabel}</label>
                              {signature ? (
                                <div
                                  style={{
                                    width: "100%",
                                    maxWidth: "280px",
                                    border: "2px solid #8b1634",
                                    borderRadius: "12px",
                                    background: "rgba(139,22,52,0.06)",
                                    padding: "12px",
                                    minHeight: "100px",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                  }}
                                >
                                  <SignatureCanvas
                                    ref={ref}
                                    penColor="#8b1634"
                                    backgroundColor="#f5eef0"
                                    clearOnResize={false}
                                    velocityFilterWeight={0.7}
                                    minWidth={1}
                                    maxWidth={2.5}
                                    dotSize={1.5}
                                    throttle={16}
                                    canvasProps={{
                                      width: 250,
                                      height: 80,
                                      style: {
                                        width: "100%",
                                        height: "80px",
                                        border: "none",
                                        borderRadius: "8px",
                                        background: "#f5eef0",
                                        display: "block",
                                        touchAction: "none",
                                      },
                                    }}
                                    onEnd={() => saveSignature(key, ref)}
                                  />
                                  <div style={{ width: "100%", display: "flex", justifyContent: "flex-end", marginTop: "8px" }}>
                                    <button
                                      type="button"
                                      onClick={() => clearSignature(key, ref)}
                                      style={{
                                        padding: "4px 12px",
                                        borderRadius: "6px",
                                        border: "1px solid #8b1634",
                                        background: "transparent",
                                        color: "#8b1634",
                                        fontSize: "12px",
                                        fontWeight: 500,
                                        cursor: "pointer",
                                      }}
                                    >
                                      Clear
                                    </button>
                                  </div>
                                </div>
                              ) : textarea ? (
                                <textarea
                                  className="form-control"
                                  rows={3}
                                  value={newForm[key] || ""}
                                  onChange={(e) => setNewForm({ ...newForm, [key]: e.target.value })}
                                  style={{ ...inputStyle, resize: "vertical" }}
                                  {...focusHandlers}
                                />
                              ) : type === "date" ? (
                                <DatePicker
                                  selected={newForm[key] ? new Date(newForm[key]) : null}
                                  onChange={(d) => setNewForm({ ...newForm, [key]: d ? d.toISOString().slice(0, 10) : "" })}
                                  dateFormat="dd-MM-yyyy"
                                  className="form-control"
                                  style={inputStyle}
                                  placeholderText="Select date"
                                />
                              ) : type === "time" ? (
                                <input
                                  type="time"
                                  className="form-control"
                                  value={newForm[key] || ""}
                                  onChange={(e) => setNewForm({ ...newForm, [key]: e.target.value })}
                                  style={inputStyle}
                                  {...focusHandlers}
                                />
                              ) : (
                                <input
                                  type="text"
                                  className="form-control"
                                  value={newForm[key] || ""}
                                  onChange={(e) => setNewForm({ ...newForm, [key]: e.target.value })}
                                  style={inputStyle}
                                  {...focusHandlers}
                                />
                              )}
                            </div>
                          );
                        })}
                        {sectionKey === "resolution" && (
                          <div className="col-12">
                            <label className="form-label" style={labelStyle}>
                              Signature of TTSL Director
                            </label>
                            <div
                              style={{
                                padding: "12px 14px",
                                borderRadius: "10px",
                                background: "#f8fafc",
                                border: "1px dashed rgba(139,22,52,0.35)",
                                color: "#64748b",
                                fontSize: "13px",
                                lineHeight: 1.5,
                              }}
                            >
                              Not added here. After saving, use the <strong>email</strong> icon on the list to send the
                              Director a link to sign.
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="modal-footer" style={{ borderTop: "1px solid #e2e8f0", padding: "16px 20px", background: "#f8fafc", borderRadius: "0 0 12px 12px" }}>
                <button type="button" className="btn" onClick={() => setShowAddModal(false)} style={{ border: "1px solid #8b1634", color: "#8b1634", background: "transparent", fontWeight: 600, borderRadius: "8px", padding: "8px 20px" }}>
                  Cancel
                </button>
                <button type="button" className="btn" style={{ background: "#8b1634", color: "#fff", fontWeight: 600, borderRadius: "8px", padding: "8px 24px", border: "none", boxShadow: "0 2px 8px rgba(139,22,52,0.3)" }} onClick={handleAdd}>
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditModal && editForm && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.4)" }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{ maxWidth: "1200px", margin: "0 auto", borderRadius: "12px", boxShadow: "0 20px 60px rgba(0,0,0,0.2)", border: "none" }}>
              <div className="modal-header" style={{ borderBottom: "1px solid #e2e8f0", background: "#f8fafc", padding: "16px 20px", borderRadius: "12px 12px 0 0" }}>
                <h5 className="modal-title" style={{ fontWeight: 600, color: "#8b1634", fontSize: "18px" }}>Edit SF-005 Refusal To Work Form</h5>
                <button type="button" className="btn-close" onClick={() => { setShowEditModal(false); setEditForm(null); }} style={{ filter: "none", opacity: 0.7 }} />
              </div>
              <div className="modal-body" style={{ maxHeight: "75vh", overflowY: "auto", padding: "24px 20px", background: "#fff" }}>
                <div style={{ padding: "14px 16px", borderRadius: "10px", background: "rgba(139,22,52,0.08)", border: "1px solid rgba(139,22,52,0.3)", color: "#8b1634", fontSize: "14px", marginBottom: "24px", fontWeight: 500 }}>
                  To be completed by the TTSL Site Supervisor. Copy to be sent to the TTSL Directors / HSQE Manager
                </div>
                {sectionOrder.map((sectionKey) => {
                  const fields = formFields.filter((f) => f.section === sectionKey);
                  const readOnlyDirectorFields = directorOnlyReadOnlyFields.filter((f) => f.section === sectionKey);
                  if (sectionKey === "resolution") return null;
                  const showSection =
                    fields.length > 0 || readOnlyDirectorFields.length > 0;
                  if (!showSection) return null;
                  const label = sectionLabels[sectionKey];
                  return (
                    <div
                      key={sectionKey}
                      style={{
                        marginBottom: "24px",
                        borderRadius: "12px",
                        border: "1px solid #e2e8f0",
                        overflow: "hidden",
                        background: "#fff",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                      }}
                    >
                      <div
                        style={{
                          padding: "12px 20px",
                          background: "linear-gradient(135deg, rgba(139,22,52,0.08) 0%, rgba(139,22,52,0.04) 100%)",
                          borderBottom: "2px solid #8b1634",
                          fontWeight: 700,
                          color: "#8b1634",
                          fontSize: "14px",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {label}
                      </div>
                      <div className="row g-3" style={{ padding: "20px" }}>
                        {sectionKey === "resolution" && (
                          <div className="col-12">
                            <div
                              style={{
                                padding: "10px 12px",
                                borderRadius: "8px",
                                background: "#f8fafc",
                                border: "1px dashed rgba(139,22,52,0.35)",
                                color: "#64748b",
                                fontSize: "13px",
                              }}
                            >
                              To be completed by TTSL SMT / Directors (read-only here). Use the{" "}
                              <strong>email</strong> icon on the list to send the Director signature link.
                            </div>
                          </div>
                        )}
                        {readOnlyDirectorFields.map(({ key, label: fieldLabel, textarea, type }) => {
                          const raw = editForm[key];
                          const display =
                            type === "date"
                              ? formatDate(raw)
                              : raw != null && String(raw).trim()
                                ? String(raw)
                                : "—";
                          return (
                            <div key={key} className="col-12">
                              <label className="form-label" style={labelStyle}>
                                {fieldLabel}
                              </label>
                              <div style={{ ...readOnlyValueStyle, minHeight: textarea ? "80px" : "42px" }}>
                                {display}
                              </div>
                            </div>
                          );
                        })}
                        {fields.map(({ key, label: fieldLabel, type, textarea, signature }) => {
                          const sigVal = editForm[key];
                          return (
                            <div key={key} className={textarea ? "col-12" : signature ? "col-md-6 col-lg-4" : "col-md-4"}>
                              <label className="form-label" style={labelStyle}>{fieldLabel}</label>
                              {signature ? (
                                <div
                                  style={{
                                    width: "100%",
                                    maxWidth: "280px",
                                    border: "2px solid #8b1634",
                                    borderRadius: "12px",
                                    background: "rgba(139,22,52,0.06)",
                                    padding: "12px",
                                    minHeight: "100px",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "center",
                                  }}
                                >
                                  {sigVal ? (
                                    <img
                                      src={sigVal.startsWith("data:image") ? sigVal : `data:image/png;base64,${sigVal}`}
                                      alt="Signature"
                                      style={{
                                        maxWidth: "100%",
                                        maxHeight: "80px",
                                        objectFit: "contain",
                                        borderRadius: "8px",
                                        padding: "4px",
                                        backgroundColor: "white",
                                        border: "1px solid #e2e8f0",
                                      }}
                                      onError={(e) => { e.target.style.display = "none"; }}
                                    />
                                  ) : (
                                    <div style={{ color: "#94a3b8", fontSize: "13px", padding: "16px" }}>No signature</div>
                                  )}
                                </div>
                              ) : textarea ? (
                                <textarea
                                  className="form-control"
                                  rows={3}
                                  value={editForm[key] || ""}
                                  onChange={(e) => setEditForm({ ...editForm, [key]: e.target.value })}
                                  style={{ ...inputStyle, resize: "vertical" }}
                                  {...focusHandlers}
                                />
                              ) : type === "date" ? (
                                <DatePicker
                                  selected={editForm[key] ? new Date(editForm[key]) : null}
                                  onChange={(d) => setEditForm({ ...editForm, [key]: d ? d.toISOString().slice(0, 10) : "" })}
                                  dateFormat="dd-MM-yyyy"
                                  className="form-control"
                                  style={inputStyle}
                                  placeholderText="Select date"
                                />
                              ) : type === "time" ? (
                                <input
                                  type="time"
                                  className="form-control"
                                  value={editForm[key] || ""}
                                  onChange={(e) => setEditForm({ ...editForm, [key]: e.target.value })}
                                  style={inputStyle}
                                  {...focusHandlers}
                                />
                              ) : (
                                <input
                                  type="text"
                                  className="form-control"
                                  value={editForm[key] || ""}
                                  onChange={(e) => setEditForm({ ...editForm, [key]: e.target.value })}
                                  style={inputStyle}
                                  {...focusHandlers}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                <div className="row g-3" style={{ padding: "20px", marginBottom: "24px" }}>
                  <div className="col-12">
                    <label className="form-label" style={labelStyle}>
                      Date issue resolved / Closed Out:
                    </label>
                    <div style={{ ...readOnlyValueStyle, minHeight: "42px" }}>
                      {editForm.dateResolvedClosedOut ? formatDate(editForm.dateResolvedClosedOut) : "—"}
                    </div>
                  </div>

                  <div className="col-12">
                    <label className="form-label" style={labelStyle}>
                      Signature of TTSL Director
                    </label>
                    {hasDirectorSignature(editForm) ? (
                      <div
                        style={{
                          maxWidth: 280,
                          border: "2px solid #8b1634",
                          borderRadius: 12,
                          background: "rgba(139,22,52,0.06)",
                          padding: 12,
                          minHeight: 100,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <img
                          src={
                            editForm.directorSignature.startsWith("data:image")
                              ? editForm.directorSignature
                              : `data:image/png;base64,${editForm.directorSignature}`
                          }
                          alt="Director signature"
                          style={{ maxWidth: "100%", maxHeight: 80, objectFit: "contain" }}
                        />
                      </div>
                    ) : (
                      <div
                        style={{
                          padding: "12px 14px",
                          borderRadius: 10,
                          background: "#f8fafc",
                          border: "1px dashed rgba(139,22,52,0.35)",
                          color: "#64748b",
                          fontSize: 13,
                        }}
                      >
                        Pending. Use the <strong>email</strong> icon on the list to request Director signature.
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={{ borderTop: "1px solid #e2e8f0", padding: "16px 20px", background: "#f8fafc", borderRadius: "0 0 12px 12px" }}>
                <button type="button" className="btn" onClick={() => { setShowEditModal(false); setEditForm(null); }} style={{ border: "1px solid #8b1634", color: "#8b1634", background: "transparent", fontWeight: 600, borderRadius: "8px", padding: "8px 20px" }}>
                  Cancel
                </button>
                <button type="button" className="btn" style={{ background: "#8b1634", color: "#fff", fontWeight: 600, borderRadius: "8px", padding: "8px 24px", border: "none", boxShadow: "0 2px 8px rgba(139,22,52,0.3)" }} onClick={handleEditSave}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AdminTaylorImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Upload SF-005 Refusal To Work Form File"
        importPath="refusal-to-work-form/import-form"
        isAdminUser={isAdminUser}
        onImported={({ insertedRow }) => {
          if (insertedRow && insertedRow._id) {
            setForms((prev) => [insertedRow, ...prev]);
            setPage(1);
          }
        }}
      />

      {showDeleteModal && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ maxWidth: "1200px", margin: "0 auto" }}>
              <div className="modal-header">
                <h5 className="modal-title">Delete SF-005 Refusal To Work Form</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this record?</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default RefusalToWorkFormPage;
